import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { Typography, Button, Input } from '../../kit';
import { Close } from '@mui/icons-material';
import { USER_TYPES } from './config';
import { useTranslation } from '@/kit/useTranslation';

function EditFieldModal({ open, field, value, onSave, onClose, loading, user }) {
  const { t } = useTranslation();
  const [editValue, setEditValue] = useState(value || '');
  const [userType, setUserType] = useState(user?.userType || USER_TYPES.INDIVIDUAL);
  const [businessName, setBusinessName] = useState(user?.businessName || '');
  const [businessGstinPan, setBusinessGstinPan] = useState(user?.businessGstinPan || '');
  const [businessAddress, setBusinessAddress] = useState(user?.businessAddress || '');
  const [kycStatus, setKycStatus] = useState(user?.kycStatus || '');

  useEffect(() => {
    setEditValue(value || '');
    setUserType(user?.userType || USER_TYPES.INDIVIDUAL);
    setBusinessName(user?.businessName || '');
    setBusinessGstinPan(user?.businessGstinPan || '');
    setBusinessAddress(user?.businessAddress || '');
    setKycStatus(user?.kycStatus || '');
  }, [value, field, user]);

  const isUserTypeField = field?.key === 'userType';
  const handleSave = () => {
    if (isUserTypeField) {
      if (userType === USER_TYPES.BUSINESS) {
        onSave({
          userType,
          businessName,
          businessGstinPan,
          businessAddress,
          kycStatus,
        });
      } else {
        onSave({ userType });
      }
    } else {
      onSave(editValue);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            type="h3"
            text={t('profile.editField', {
              field: field?.labelKey ? t(field.labelKey) : field?.label,
            })}
          />
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close edit dialog"
            sx={(theme) => ({ color: theme.palette.text.secondary })}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {isUserTypeField ? (
          <>
            <RadioGroup row value={userType} onChange={(e) => setUserType(e.target.value)}>
              <FormControlLabel
                value={USER_TYPES.INDIVIDUAL}
                control={<Radio />}
                label={<Typography type="text">{t('profile.individual')}</Typography>}
              />
              <FormControlLabel
                value={USER_TYPES.BUSINESS}
                control={<Radio />}
                label={<Typography type="text">{t('profile.business')}</Typography>}
              />
            </RadioGroup>
            {userType === USER_TYPES.BUSINESS && (
              <>
                <Input
                  label={t('profile.businessName')}
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  fullWidth
                />
                <Input
                  label={t('profile.businessGstinPan')}
                  value={businessGstinPan}
                  onChange={(e) => setBusinessGstinPan(e.target.value)}
                  fullWidth
                />
                <Input
                  label={t('profile.businessAddress')}
                  value={businessAddress}
                  onChange={(e) => setBusinessAddress(e.target.value)}
                  fullWidth
                />
                <Input
                  label={t('profile.kycStatus')}
                  value={kycStatus}
                  onChange={(e) => setKycStatus(e.target.value)}
                  fullWidth
                />
              </>
            )}
          </>
        ) : (
          <Input
            label={field?.labelKey ? t(field.labelKey) : field?.label}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            fullWidth
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          {t('common.cancel')}
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          {loading ? t('profile.saving') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditFieldModal;
